import { useQuery, keepPreviousData } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api-client";
import type { ScoringResultsListResponse } from "@/hooks/queries/scoring-types";

// Scoring-results list query hook (Story 9.14, consuming the 9.14 list endpoint).
// Mirrors `useListEvaluations`: `apiFetch` + `useQuery`, a granular query key, a
// manual path build so the exact URL is assertable. `apiFetch` unwraps the
// `{ data: {...} }` envelope, so the result is `PaginatedData<ScoringResultListItem>`.
//
// Deliberately NO IDB fallback (unlike the gh-471 evaluations list): scoring
// results have no local table to reconstruct from. Like every other online-only
// list (`useListClients`, `useListEvaluations`), this sets `networkMode: "always"`
// + `retry: false` — under the default "online" mode an offline browser PAUSES the
// query (pending forever, no error), which is the gh-471 infinite-spinner trap.
// "always" lets the queryFn fail fast so the consumer renders `OfflineListNotice`
// (AC7) — an honest notice, never a false "No recent results yet." empty state.

/** Root key for every scoring-results list query — `invalidateQueries([SCORING_RESULTS_QUERY_KEY])` matches all. */
export const SCORING_RESULTS_QUERY_KEY = "scoring-results";

/** Backend defaults (mirror `list_scoring_results` query params: page≥1, 1≤limit≤100). */
const DEFAULT_PAGE = 1;
const DEFAULT_LIMIT = 20;

export interface ListScoringResultsParams {
  /** 1-indexed page number (backend default 1). */
  page?: number;
  /** Page size (backend default 20, max 100). */
  limit?: number;
  /** Restrict to one client (UUID). Omitted ⇒ all of the caller's results. */
  clientId?: string;
  /** Inclusive lower bound on created_at (ISO date, e.g. "2026-07-01"). */
  dateFrom?: string;
  /** Inclusive upper bound on created_at (ISO date). */
  dateTo?: string;
}

/** Build the `/scoring-results` query string manually so the exact URL is assertable. */
function buildPath(
  page: number,
  limit: number,
  clientId: string | undefined,
  dateFrom: string | undefined,
  dateTo: string | undefined,
): string {
  const search = new URLSearchParams({ page: String(page), limit: String(limit) });
  if (clientId) search.set("client_id", clientId);
  if (dateFrom) search.set("date_from", dateFrom);
  if (dateTo) search.set("date_to", dateTo);
  return `/scoring-results?${search.toString()}`;
}

export function useListScoringResults(params: ListScoringResultsParams = {}) {
  const page = params.page ?? DEFAULT_PAGE;
  const limit = params.limit ?? DEFAULT_LIMIT;
  const { clientId, dateFrom, dateTo } = params;

  return useQuery({
    queryKey: [
      SCORING_RESULTS_QUERY_KEY,
      { page, limit, clientId, dateFrom, dateTo },
    ] as const,
    queryFn: () =>
      apiFetch<ScoringResultsListResponse>(
        buildPath(page, limit, clientId, dateFrom, dateTo),
      ),
    // Keep the current page visible while the next loads (no loading flash on paging).
    placeholderData: keepPreviousData,
    // Online-only list: fail fast when offline instead of pausing pending-forever
    // (gh-471 — see the header comment). Retries would just queue 3 more doomed
    // transport failures before the offline notice can render.
    networkMode: "always",
    retry: false,
  });
}

/**
 * The Monday that starts `now`'s LOCAL calendar week, as a plain ISO date
 * ("YYYY-MM-DD"). Monday-start week: Sunday belongs to the week that began the
 * PREVIOUS Monday. Local fields (not UTC) so the clinician's "this week" matches
 * her wall calendar; exported for the hook test and any future date-filter UI.
 *
 * Known precision bound (code-review-plus 2026-07-17, accepted): the backend
 * anchors this date at UTC midnight, so the week boundary drifts by the
 * clinician's UTC offset — a few boundary hours over/under-counted on the KPI.
 * Cosmetic for a summary tile; revisit (datetime-bounds contract) if the 9.13
 * date-filter UI needs true local precision.
 */
export function mondayOfCurrentWeekISO(now: Date = new Date()): string {
  const monday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  // getDay(): 0 = Sunday … 6 = Saturday → days to step back to reach Monday.
  monday.setDate(monday.getDate() - ((now.getDay() + 6) % 7));
  const y = monday.getFullYear();
  const m = String(monday.getMonth() + 1).padStart(2, "0");
  const d = String(monday.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/**
 * Count of the caller's results since Monday of the current local week — the cheap
 * query behind the "Results this week" KPI (Screen 02, AC8). Asks for the smallest
 * possible page (`limit=1`) and reads only `count`, so the tile costs one tiny
 * request (mirrors `useEvaluationStatusCount`). Returns the unwrapped query so the
 * caller maps loading / error (→ "—") itself.
 */
export function useResultsThisWeekCount() {
  return useListScoringResults({ limit: 1, dateFrom: mondayOfCurrentWeekISO() });
}
